import { RecentlyViewed } from '@theme/recently-viewed-products';

class RecentlyViewedProducts extends HTMLElement {
  /**
   * The observer for lazy loading the section
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecentlyViewed();
    },
    { rootMargin: '0px 0px 400px 0px' },
  );

  /**
   * Observing changes to the elements attributes (for theme editor)
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-search-performed attribute has been set to 'true'
      if (mutation.attributeName === 'data-search-performed' && this.dataset.searchPerformed === 'true') continue;

      // All other attribute changes trigger a reload
      this.#loadRecentlyViewed();
      break;
    }
  });

  /**
   * An abort controller for the active fetch
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Load the recently viewed products
   */
  #loadRecentlyViewed() {
    const { sectionId, searchPerformed } = this.dataset;
    const id = this.id;

    if (!id || !sectionId) {
      throw new Error('Section ID and an ID attribute are required');
    }

    // If the search has already been performed, no need to reload
    if (searchPerformed === 'true') {
      return;
    }

    const viewedProducts = RecentlyViewed.getProducts();

    // If no recently viewed products, hide the section
    if (viewedProducts.length === 0) {
      this.#handleEmpty();
      return;
    }

    this.#fetchRecentlyViewed(viewedProducts, sectionId)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        // Content is from Shopify's search endpoint (same-origin, server-rendered)
        html.innerHTML = result.data || '';
        const recentlyViewed = html.querySelector(`recently-viewed-products[id="${id}"]`);

        if (recentlyViewed?.innerHTML && recentlyViewed.innerHTML.trim().length) {
          // Check if the fetched content actually has products
          const hasProducts = recentlyViewed.querySelector('.resource-list__item');
          if (hasProducts) {
            this.dataset.searchPerformed = 'true';
            // Server-rendered content from Shopify's Section Rendering API
            this.innerHTML = recentlyViewed.innerHTML;
          } else {
            this.#handleEmpty();
          }
        } else {
          this.#handleEmpty();
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recently viewed products via search
   * @param {string[]} productIds - Array of product IDs
   * @param {string} sectionId - The section ID
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchRecentlyViewed(productIds, sectionId) {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString(), { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      return { success: true, data: text };
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        return { success: false, status: 0 };
      }
      throw e;
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle empty state (no recently viewed products)
   */
  #handleEmpty() {
    this.classList.add('hidden');
    this.dataset.empty = 'true';
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Recently viewed products error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading recently viewed products';
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
